import React, { useState, JSX, useRef, useEffect } from "react";
import {
  ActivityTableReport,
  ReportsBalance,
  ReportsGraphArea,
  ReportsGraphAreaFee,
} from "@/components";
import { AnimatePresence, motion } from "framer-motion";
import { isBitramp } from "@/utils/utils";

// TODO - Use enum for reports
const reports = ["Volume", "Fees", "RecentActivities", "Balance"];

const Reports: React.FC = () => {
  const [openSections, setOpenSections] = useState<{ [key: string]: boolean }>(
    {}
  );
  const [startDate, setStartDate] = useState<number>(7);
  const [filterDateOpen, setFilterDateOpen] = useState<boolean>(false);
  const filterDateRef = useRef<HTMLDivElement>(null);
  const reportsRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        reportsRef.current &&
        !reportsRef.current.contains(event.target as Node)
      ) {
        // Închide toate secțiunile
        setOpenSections({});
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        filterDateRef.current &&
        !filterDateRef.current.contains(event.target as Node)
      ) {
        setFilterDateOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const toggleSection = (section: string) => {
    setOpenSections((prev) => ({ ...prev, [section]: !prev[section] }));
  };

  const reportComponents: { [key: string]: JSX.Element } = {
    Volume: <ReportsGraphArea startDate={startDate} />,
    Fees: <ReportsGraphAreaFee startDate={startDate} />,
    RecentActivities: <ActivityTableReport isReport={true} />,
    Balance: <ReportsBalance />,
  };

  return (
    <div className="min-h-screen mb-20">
      <div>
        <div className="flex justify-between items-center p-4">
          <h2 className="Heading2 bold dark-blue">Reports</h2>
          <div
            className={`relative  py-2 Container border-none focus:ring-0 pshadow cursor-pointer w-fit ps-4 pe-8 Input ${
              isBitramp() ? "!bg-[#2b2b2b]" : ""
            }`}
            ref={filterDateRef}
          >
            <div
              onClick={() => setFilterDateOpen(!filterDateOpen)}
              className="h-full flex justify-start items-center gap-2 cursor-pointer"
            >
              {startDate === 7
                ? "Last 7 days"
                : startDate === 30
                ? "Last 30 days"
                : startDate === 365
                ? "Last 365 days"
                : "Custom"}
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="11"
                height="10"
                viewBox="0 0 11 10"
                fill="none"
                className="absolute right-3 top-1/2 transform -translate-y-1/2"
              >
                <path
                  d="M6.38133 9.35606C6.00426 10.0594 4.99573 10.0594 4.61867 9.35606L0.660254 1.97249C0.303111 1.30632 0.785718 0.5 1.54159 0.5L9.45841 0.500001C10.2143 0.500001 10.6969 1.30632 10.3397 1.97249L6.38133 9.35606Z"
                  fill={`${isBitramp() ? "#fff" : "#737791"}`}
                />
              </svg>
            </div>

            <AnimatePresence>
              {filterDateOpen && (
                <motion.div
                  initial={{ opacity: 0, y: -8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -8 }}
                  transition={{ duration: 0.2 }}
                  className={`absolute top-full left-0 w-full Container pshadow mt-1 z-10 ${
                    isBitramp()
                      ? "bg-[#2b2b2b] text-white border border-[rgba(255,255,255,0.1)]"
                      : ""
                  }`}
                >
                  <div
                    onClick={() => {
                      setStartDate(7);
                      setFilterDateOpen(false);
                    }}
                    className={`px-4 py-2  hover:rounded-[0.625rem] cursor-pointer flex justify-start align-center gap-2 ${
                      isBitramp() ? "hover:bg-[#262626]" : "hover:bg-gray-100"
                    }`}
                  >
                    Last 7 days
                  </div>
                  <div
                    onClick={() => {
                      setStartDate(30);
                      setFilterDateOpen(false);
                    }}
                    className={`px-4 py-2  hover:rounded-[0.625rem] cursor-pointer flex justify-start align-center gap-2 ${
                      isBitramp() ? "hover:bg-[#262626]" : "hover:bg-gray-100"
                    }`}
                  >
                    Last 30 days
                  </div>
                  <div
                    onClick={() => {
                      setStartDate(365);
                      setFilterDateOpen(false);
                    }}
                    className={`px-4 py-2  hover:rounded-[0.625rem] cursor-pointer flex justify-start align-center gap-2 ${
                      isBitramp() ? "hover:bg-[#262626]" : "hover:bg-gray-100"
                    }`}
                  >
                    Last year
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>

        <div className="w-[100%] mx-auto Container pshadow" ref={reportsRef}>
          <div className="py-6">
            {reports.map((report) => (
              <div key={report} className="px-8 py-2">
                <button
                  className={`border p-2 pl-8 Container w-[100%] flex flex-wrap justify-between items-center ${
                    isBitramp() ? "!bg-[#262626] !border-[#464646]" : ""
                  }`}
                  onClick={() => toggleSection(report)}
                >
                  <span className="Text semi-bold dark-blue">
                    {report === "RecentActivities"
                      ? "Recent Activities"
                      : report}
                  </span>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="12"
                    height="12"
                    viewBox="0 0 14 14"
                    fill="none"
                    className={`transition-transform mr-2 ${
                      openSections[report] ? "rotate-180" : ""
                    }`}
                  >
                    <path
                      d="M13.6449 0.746493L6.89462 7.4968L0.149023 0.751206L0.147976 7.04853L6.89933 13.7999L13.641 7.05821L13.6449 0.746493Z"
                      fill={`${isBitramp() ? "#EE33D9" : "#202A43"}`}
                    />
                  </svg>
                </button>
                {openSections[report] && (
                  <div className="p-4 text-gray-600">
                    {reportComponents[report]}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Reports;
